import { Box, Flex, font, isMobile as isMobileUA, themedColor, useCollapsible } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { useRef, type FC } from 'react';
import styled from 'styled-components';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 160px);
  gap: 8px;

  ${MEDIA_QUERIES.tablet`
    grid-template-columns: repeat(3, 250px);
    gap: 12px;
  `}

  ${MEDIA_QUERIES.desktop`
    grid-template-columns: repeat(4, 250px);
    gap: 12px;
  `}
`;

const ShowMoreButton = styled.button<{ isClosed: boolean }>`
  border: none;
  background: none;
  display: block;
  padding: 2px 5px;
  margin: 16px auto 0;
  color: ${themedColor('textPrimary')};
  font-size: 1.6rem;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;

  &:after {
    content: '';
    display: inline-block;
    width: calc(100% - 10px);
    height: 1px;
    border-top: 1px solid ${themedColor('invertedTextSecondary')};
    position: absolute;
    bottom: 1px;
    left: 5px;
  }

  &:hover {
    color: ${themedColor('textSecondary')};
  }
`;

const Slide = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  width: 160px;
  height: 160px;
  flex: 0 0 160px;
  padding: 14px;
  border-radius: 8px;
  background-color: ${themedColor('white')};
  ${font('main', { fontSize: '1.4rem', lineHeight: '1.9rem' })}

  p {
    text-wrap: balance;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: 250px;
    height: 250px;
    flex: 0 0 250px;
    padding: 34px 32px 24px;
    border-radius: 16px;
    ${font('main', { fontSize: '2rem', lineHeight: '2.4rem' })}
  `}
`;

const Bold = styled.span`
  text-wrap: pretty;
  font-weight: 700;
  display: inline-block;ּ
`;

const Desc = styled.span`
  display: inline-block;
`;

const SlideOrg = styled.div`
  color: ${themedColor('textSecondary')};
  ${font('main', { fontSize: '1rem' })}

  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontSize: '1.3rem', fontWeight: 700 })}
  `}
`;

export interface ImpactSlideProps {
  title: string;
  desc: string;
  org: string;
}

const ImpactSlide: FC<ImpactSlideProps> = ({ title, desc, org }) => {
  return (
    <Slide>
      <p>
        <Bold>{title}&nbsp;</Bold>
        <Desc>{desc}</Desc>
      </p>
      <SlideOrg>{org}</SlideOrg>
    </Slide>
  );
};

interface ImpactSliderProps {
  charities: ImpactSlideProps[];
}

export const ImpactSlider: FC<ImpactSliderProps> = ({ charities }) => {
  const isMobile = isMobileUA();
  const ref = useRef<HTMLDivElement>(null);
  const slideHeight = isMobile ? 160 : 250;
  const gap = isMobile ? 8 : 12;
  const rows = isMobile ? 3 : 2;
  const totalRows = isMobile ? 20 : 10;

  const { collapsibleWrapperStyles, isClosed, handleToggleCollapsible } = useCollapsible({
    ref,
    isCollapsed: true,
    extendedCollapsibleWrapperStyles: closed => ({
      height: `${(slideHeight + gap) * totalRows}px`,
      maxHeight: closed ? `${(slideHeight + gap) * rows}px` : `${(slideHeight + gap) * totalRows}px`,
      opacity: '1',
    }),
  });

  const handleCollapse = () => {
    handleToggleCollapsible();
    if (!isClosed) {
      ref?.current?.scrollIntoView(true);
    }
  };

  return (
    <Box my={[40, 50]}>
      <Box style={isMobile ? collapsibleWrapperStyles : undefined}>
        <Grid ref={ref}>
          {charities.map(item => (
            <ImpactSlide key={item.title} {...item} />
          ))}
        </Grid>
      </Box>
      {isMobile && (
        <ShowMoreButton onClick={handleCollapse} isClosed={isClosed}>
          {isClosed ? 'Show all' : 'Show less'}
        </ShowMoreButton>
      )}
    </Box>
  );
};
